import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您查找的文章可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://galgame-resource.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'Galgame资源导航',
    },
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章', path: '/' },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-2">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
              <span>{article.author}</span>
              <span>&middot;</span>
              <span>{article.date}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
            <div className="h-64 md:h-80 rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={article.thumbnailUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </section>

          {/* Article Content */}
          <article className="max-w-3xl mx-auto px-4 md:px-6 pb-14">
            <div
              className="prose prose-sm max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-xl prose-h3:text-lg
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-li:text-foreground-700 prose-li:leading-relaxed
                prose-strong:text-foreground-900 prose-strong:font-medium
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                prose-ol:my-4 prose-ul:my-4
                [&_h2]:border-l-4 [&_h2]:border-primary-400 [&_h2]:pl-4
                [&_h3]:border-l-2 [&_h3]:border-accent-300 [&_h3]:pl-3
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5
                [&_li]:mb-1"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 transition-all duration-300"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={related.thumbnailUrl}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 text-xs text-foreground-400 mb-1.5">
                          <span>{related.date}</span>
                          <span>&middot;</span>
                          <span>{related.readTime}</span>
                        </div>
                        <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}